import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useMappedState } from "redux-react-hook";
import { Link, Redirect } from "react-router-dom";
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";
import {
  Alert,
  TextInput,
  TextArea,
  Button,
  Text,
  TextVariants,
  SelectOption,
  Form,
  FormGroup,
  ActionGroup,
  Tooltip,
  TooltipPosition,
  Checkbox,
  Grid,
  GridItem,
} from "@patternfly/react-core";
import TrashIcon from "@patternfly/react-icons/dist/js/icons/trash-icon";
import { OutlinedQuestionCircleIcon } from "@patternfly/react-icons";
import { Spinner } from "@patternfly/react-core";
import {
  REQUESTED_PRODUCTS,
  REQUESTED_CREATE_PRODUCT,
} from "../../redux/actions/productAction";
import { REQUESTED_USERS } from "../../redux/actions/userAction";
// import { REQUESTED_ACTIVITIES } from "../../redux/actions/activityAction";
import MultiTypeaheadSelectInput from "../general/MultipleSelect";
import Toast from "../../common/toast";

export default (props) => {
  const mapState = useCallback((state) => {
    return {
      isCreatingProduct: state.productState.isCreatingProduct,
      isCreatedProduct: state.productState.isCreatedProduct,
      createProductError: state.productState.createProductError,
      users: state.userState.users,
    };
  }, []);

  const [cookies, setCookie] = useCookies(["selectedProducts"]);

  const {
    isCreatingProduct,
    isCreatedProduct,
    users,
    createProductError,
  } = useMappedState(mapState);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!users) {
      dispatch({
        type: REQUESTED_USERS,
        payload: {},
      });
    }
  }, [dispatch, users]);

  const helperText = (
    <>
      <Text
        component={TextVariants.a}
        href="https://docs.google.com/spreadsheets/d/1DLS_lS3VKidgZIvcLmLp9BoiqptkvqHWfe1D5FD2kfk/edit"
        target="_blank"
      >
        Master product name list
      </Text>
      <div style={{ marginLeft: "0.5rem", display: "inline-block" }}>
        <Tooltip
          isContentLeftAligned
          position={TooltipPosition.right}
          content={
            <>
              <Text component={TextVariants.p}>
                When adding a product, please consult the master product name
                list.
              </Text>
              <Text component={TextVariants.p}>
                If your product is not listed, or listed differently than your
                understanding, please email naming@redhat.com and your BU
                partners.
              </Text>
            </>
          }
        >
          <OutlinedQuestionCircleIcon />
        </Tooltip>
      </div>
    </>
  );

  const userSelectOptions = users
    ? users.map((user) => (
        <SelectOption key={`user-${user.id}`} value={user.kerberos} />
      ))
    : [<SelectOption key={`default`} value={""} />];

  const [productName, setProductName] = useState("");
  const [productLink, setProductLink] = useState("");
  const [productAcronyms, setProductAcronyms] = useState("");
  const [productRelatedLinks, setProductRelatedLinks] = useState("");
  const [productIsLayered, setProductIsLayered] = useState(false);
  const [productCreated, setProductCreated] = useState(false);
  const [editUsers, setEditUsers] = useState();
  const [errorMsg, setErrorMsg] = useState(
    props.errorMsg ? props.errorMsg : ""
  );
  const [extraHeaders, setExtraHeaders] = useState([""]);
  const [isShowRemove, setIsShowRemove] = useState(false);
  const [productExtraHeader, setProductExtraHeader] = useState("");

  useEffect(() => {
    if (extraHeaders.length > 1) {
      setIsShowRemove(true);
    } else {
      setIsShowRemove(false);
    }
    setProductExtraHeader(extraHeaders[0]);
  }, [extraHeaders]);

  function addHeader() {
    setExtraHeaders((s) => {
      return [...s, ""];
    });
  }

  function handleChange(value, index) {
    let newExtraHeaders = [...extraHeaders];
    newExtraHeaders[index] = value;
    setExtraHeaders(newExtraHeaders);
  }

  function removeHeader(index) {
    let newExtraHeaders = [...extraHeaders];
    newExtraHeaders.splice(index, 1);
    setExtraHeaders(newExtraHeaders);
  }

  function onSubmit(is_draft) {
    let createdata = {
      name: productName,
      acronyms: productAcronyms,
      link: productLink,
      related_links: productRelatedLinks,
      is_draft: is_draft,
      is_layered_product: productIsLayered,
      extra_header: productExtraHeader,
      extra_headers: extraHeaders,
      uuid: uuidv4(),
    };

    if (editUsers && editUsers.length !== 0) {
      createdata = { ...createdata, add_users: editUsers };
    }

    // Prepare to set cookie
    let selectedProducts = [];
    if (cookies.selectedProducts && cookies.selectedProducts.length > 0) {
      selectedProducts = [
        ...new Set([...cookies.selectedProducts, createdata.name]),
      ];
    } else {
      selectedProducts = [createdata.name];
    }
    if (productName.trim() === "") {
      setErrorMsg("Product Name cannot be empty");
    } else if (productIsLayered && extraHeaders.every((item) => item == "")) {
      setErrorMsg("Please set at least one dependent header");
    } else {
      setCookie("selectedProducts", selectedProducts);
      dispatch({
        type: REQUESTED_CREATE_PRODUCT,
        payload: { product: createdata },
      });
    }
  }

  function renderErrorMsg() {
    if (errorMsg !== "") {
      return <Alert variant="danger" title={`${errorMsg}`} />;
    }
  }

  useEffect(() => {
    if (createProductError) {
      const { code, message } = createProductError;
      if (code == 401) {
        Toast.error(`${message}: please refresh the current page`, 3000);
      } else {
        Toast.error(message, 3000);
      }
    }
  }, [createProductError]);

  function renderButtons() {
    if (isCreatingProduct) {
      return <Spinner size="xl" />;
    }
    return (
      <>
        <Button
          variant="primary"
          disabled={isCreatingProduct}
          onClick={() => onSubmit(true)}
        >
          {`Save as Draft`}
        </Button>
        <Button
          variant="secondary"
          disabled={isCreatingProduct}
          onClick={() => onSubmit(false)}
        >
          {`Save & publish`}
        </Button>
        <Link to="/product/list">
          <Button
            variant="tertiary"
            disabled={isCreatingProduct}
          >{`Cancel`}</Button>
        </Link>
      </>
    );
  }

  useEffect(() => {
    if (isCreatedProduct) {
      dispatch({
        type: REQUESTED_PRODUCTS,
        payload: {},
      });
      setProductCreated(true);
      // dispatch({
      //   type: REQUESTED_ACTIVITIES,
      //   payload: {},
      // });
      dispatch({
        type: REQUESTED_USERS,
        payload: {},
      });
    }
  }, [dispatch, isCreatedProduct]);

  function onSelectProduct(selected) {
    setEditUsers(selected);
  }

  function renderExtraHeader() {
    if (productIsLayered) {
      return (
        <FormGroup
          label="Dependent Header Column"
          isRequired
          fieldId="product-create-form-extra-header"
          id="product-create-form-extra-header"
        >
          {extraHeaders.map((header, index) => {
            return (
              <Grid
                hasGutter
                key={`grid-${index}`}
                id={`extra-header-grid-${index}`}
                style={{ marginBottom: "16px" }}
              >
                <GridItem span={5} key={`gridItem1-${index}`}>
                  <TextInput
                    key={`textinput-${index}`}
                    onChange={(value) => {
                      handleChange(value, index);
                    }}
                    value={header}
                    id={`extra-header-${index}`}
                    type={"text"}
                  />
                </GridItem>
                {isShowRemove && (
                  <GridItem span={1} key={`gridItem2-${index}`}>
                    <Button
                      variant="plain"
                      onClick={() => {
                        removeHeader(index);
                      }}
                      aria-label="Remove"
                    >
                      <TrashIcon />
                    </Button>
                  </GridItem>
                )}
              </Grid>
            );
          })}
          <Button onClick={addHeader}>Add</Button>
        </FormGroup>
      );
    }
  }

  return (
    <>
      {productCreated ? <Redirect to="/product/list" /> : null}
      <Form>
        <FormGroup
          label="Product Name"
          isRequired
          fieldId="product-create-form-name"
          helperText={helperText}
        >
          <TextInput
            value={productName}
            isRequired
            type="text"
            id="product-create-form-name"
            aria-describedby="product-create-form-name-helper"
            name="product-create-form-name"
            onChange={(value) => {
              setProductName(value);
            }}
          />
        </FormGroup>
        <FormGroup label="Members" fieldId="product-create-form-members">
          <MultiTypeaheadSelectInput
            titleId="member-multi-select"
            placeholder="Select member"
            options={userSelectOptions}
            onSelect={(selected) => onSelectProduct(selected)}
          />
        </FormGroup>
        <FormGroup
          label="Link of detailed life cycle information"
          fieldId="product-create-form-link"
        >
          <TextInput
            value={productLink}
            onChange={(value) => {
              setProductLink(value);
            }}
            type="url"
            id="product-create-form-link"
            name="product-create-form-link"
          />
        </FormGroup>
        <FormGroup
          label="Acronyms, Short Names, Key Components"
          fieldId="product-create-form-acronyms"
        >
          <TextInput
            value={productAcronyms}
            onChange={(value) => {
              setProductAcronyms(value);
            }}
            type="text"
            id="product-create-form-acronyms"
            name="product-create-form-acronyms"
          />
        </FormGroup>
        <FormGroup
          label="Support policies"
          fieldId="product-create-form-tsp"
          helperText="Please use comma , as seperator for multiple links"
        >
          <TextArea
            value={productRelatedLinks}
            onChange={(value) => {
              setProductRelatedLinks(value);
            }}
            name="product-create-form-tsp"
            id="product-create-form-tsp"
          />
        </FormGroup>
        <FormGroup
          label="Is component product"
          fieldId="product-create-form-is-layered-product"
        >
          <Checkbox
            label="Yes"
            aria-label="Is Component Product"
            name="product-create-form-is-layered-product"
            id="product-create-form-is-layered-product"
            isChecked={!!productIsLayered}
            onChange={(isChecked) => setProductIsLayered(isChecked)}
          />
        </FormGroup>
        {renderExtraHeader()}
        {renderErrorMsg()}
        <ActionGroup>{renderButtons()}</ActionGroup>
      </Form>
    </>
  );
};
